import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { setDeviceFilters } from 'store';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Card from 'components/UI/Card';
import FoldableCard from 'components/UI/FoldableCard';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import PageWrapper from 'components/UI/PageWrapper';
import SearchField from 'components/UI/SearchField';
import CardHeader from 'components/UI/Card/CardHeader';
import Loader from 'components/UI/Loader';
import Button from 'components/UI/Button';
import {
  fetchRegistrationList,
  lwm2mListSelector,
} from 'store/lwm2mState/lwm2mState';
import { Lwm2mFilter, Lwm2mFilterAttribute } from 'models/lwm2m';
import RegistrationTable from 'components/lwm2m/RegistrationTable';

// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const Lwm2mPage: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  // const meta = useAppSelector(metaSelector);
  const { data, isLoading, filters, totalCount } =
    useAppSelector(lwm2mListSelector);

  // Mount
  useEffect(() => {
    dispatch(fetchRegistrationList([], 0));
  }, [dispatch]);

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Fetch devices
  const fetchRegistrations = useCallback(
    (lwm2mFilters: Lwm2mFilter[], offset?: number) => {
      dispatch(fetchRegistrationList(lwm2mFilters, offset || 0));
    },
    [dispatch]
  );

  // Search
  const onSearch = useCallback(
    (value: string) => {
      const searchFilters: Lwm2mFilter[] = [
        {
          attribute: Lwm2mFilterAttribute.IprId,
          value,
        },
      ];
      setPagination(initialPagination);
      if (searchFilters.length) {
        fetchRegistrations(searchFilters, initialPagination.offset);
      }
    },
    [fetchRegistrations]
  );

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      //fetchRegistrations(filters.data || [], paginate.offset);
    },
    [] // fetchRegistrations, filters.data]
  );

  // Toggle filters
  const toggleFilters = useCallback(() => {
    dispatch(setDeviceFilters(!filters.active));
  }, [dispatch, filters.active]);

  // Apply filters
  // Render results
  const results = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!data || !data.length) {
      return null;
    }
    return (
      <Card>
        <CardHeader title="lwm2m.table_title" />
        <RegistrationTable registrations={data} />
        <Pagination
          noItems={data.length}
          totalCount={totalCount || 0}
          pagination={pagination}
          setPagination={onSetPagination}
        />
      </Card>
    );
  }, [data, isLoading, pagination, totalCount, onSetPagination]);

  return (
    <PageWrapper>
      <Heading>
        <FormattedMessage id="lwm2m.title" />
      </Heading>
      <Card>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="search.title" />
          <Button backgroundColor="primary" disabled>
            Under construction
          </Button>
        </Heading>
        <SearchField
          onSearch={onSearch}
          placeholder={intl.formatMessage({
            id: 'connectivity.search_placeholder',
          })}
          buttons={
            <Button
              onClick={toggleFilters}
              disabled
              backgroundColor="surface"
              color={filters.active ? 'primaryDark' : 'onSurface'}
            >
              <FormattedMessage id="filters.btn" />
            </Button>
          }
        />
      </Card>
      <FoldableCard open={filters.active}>
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage id="filters.title" />
        </Heading>
      </FoldableCard>
      {results}
    </PageWrapper>
  );
};

export default Lwm2mPage;
